<template>
  <div :class="classes">
    <slot name="previous-control" v-bind="{ getCurrentScrollPosition, visible: previousVisible }" />
    <div
      ref="scroll-container"
      class="carousel__content"
      data-test-id="carousel-content"
      tabindex="-1"
      @scroll="onScroll"
    >
      <slot />
    </div>
    <slot name="next-control" v-bind="{ getCurrentScrollPosition, visible: nextVisible }" />
  </div>
</template>

<script>
import { throttle } from 'throttle-debounce';

import screen from '~~/common/vue/mixins/screen';

const SCROLL_THROTTLE_TIME = 100;

export const scrollStates = {
  START: 0,
  MIDDLE: 1,
  END: 2,
  NONE: 3,
};

export default {
  name: 'ECarouselBase',
  mixins: [screen('onScreenResize')],
  props: {
    snap: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    scrollState: scrollStates.NONE,
  }),
  computed: {
    previousVisible() {
      return this.scrollState !== scrollStates.NONE && this.scrollState !== scrollStates.START;
    },
    nextVisible() {
      return this.scrollState !== scrollStates.NONE && this.scrollState !== scrollStates.END;
    },
    canScroll() {
      const scrollContainer = this.$refs['scroll-container'];
      return scrollContainer.scrollWidth > scrollContainer.offsetWidth;
    },
    classes() {
      return {
        'carousel': true,
        'carousel--snap': this.snap,
      };
    },
  },
  mounted() {
    this.$refs['scroll-container'].scrollLeft = 0;

    this.throttledOnScroll = throttle(SCROLL_THROTTLE_TIME, ({ target }) => {
      const scrollPosition = this.getCurrentScrollPosition();
      if (scrollPosition <= 0) {
        this.scrollState = scrollStates.START;
      } else if (scrollPosition >= target.scrollWidth - target.offsetWidth) {
        this.scrollState = scrollStates.END;
      } else {
        this.scrollState = scrollStates.MIDDLE;
      }
    });
  },
  methods: {
    getCurrentScrollPosition() {
      return this.$refs['scroll-container'].scrollLeft;
    },
    onScreenResize() {
      if (this.canScroll) {
        if (this.scrollState === scrollStates.NONE) {
          // we've probably just rendered for the first time
          this.scrollState = scrollStates.START;
        }
      } else {
        this.scrollState = scrollStates.NONE;
      }
    },
    onScroll(event) {
      this.throttledOnScroll(event);
    },
  },
};
</script>

<style lang="scss" scoped>
.carousel {
  display: flex;
  position: relative;
  align-items: center;
}

.carousel__content {
  overflow-x: scroll;
  position: relative;
  scrollbar-width: none;
  flex-grow: 1;

  &::-webkit-scrollbar {
    width: 0;
    height: 0;
  }

  .carousel--snap & {
    scroll-snap-type: x mandatory;
  }
}

.carousel__content:focus {
  @extend %keyboard-nav-focus;
}
</style>

<docs>
This is a base component for interactive carousels. It could become
a common component once we need it elsewhere (for instance the Web vertical).
Its only responsibility is to render the content, previous/next buttons,
and trigger events/update button visibility based on the scroll state.

To use it, populate the named slots for buttons and the default slots for content.
Then you can bind the `previous` and `next` events to react to button interactions.
To scroll the content you provided, you can for instance update its CSS,
or use DOM methods such as `scrollIntoView()`.
</docs>
